// Reactions feature API client.
// Toggles a Teams/Slack-style emoji reaction on a post or comment.
// Types mirror api/schemas/reactions.py ReactionSummary / ReactionToggleResponse.
//
// Unlike VoteControl's single toggleable upvote, a user may hold several
// *different* emoji on the same target at once — each emoji is toggled
// independently via its own POST.
import { apiClient } from "@/lib/api-client";

export type ReactionTarget = "post" | "comment" | "training" | "training_comment";

export interface ReactionSummary {
  emoji: string;
  count: number;
  reacted_by_me: boolean;
}

export interface ReactionToggleResponse {
  target_type: string;
  target_id: string;
  reactions: ReactionSummary[];
}

/**
 * Toggle the current user's `emoji` reaction on a post or comment.
 *
 * Requires an authenticated session cookie — the browser sends it
 * automatically. Throws ApiError on 401 (unauthenticated) or network errors.
 */
export async function toggleReaction(
  target: ReactionTarget,
  targetId: string,
  emoji: string,
): Promise<ReactionToggleResponse> {
  const pathByTarget: Record<ReactionTarget, string> = {
    post: `/api/v1/reactions/posts/${encodeURIComponent(targetId)}`,
    comment: `/api/v1/reactions/comments/${encodeURIComponent(targetId)}`,
    training: `/api/v1/reactions/training/${encodeURIComponent(targetId)}`,
    training_comment: `/api/v1/reactions/training-comments/${encodeURIComponent(targetId)}`,
  };
  const path = pathByTarget[target];

  return apiClient<ReactionToggleResponse>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ emoji }),
  });
}
